/**
 * Unsaved Changes Hook
 * Tracks unsaved changes and warns before navigation
 */

import { useState, useEffect, useCallback, useRef } from 'react'

export interface UseUnsavedChangesReturn {
  hasUnsavedChanges: boolean
  markAsSaved: () => void
  markAsUnsaved: () => void
  warnBeforeLeaving: (message?: string) => boolean | Promise<boolean>
  resetTracking: () => void
}

interface UseUnsavedChangesOptions {
  /** Enable browser beforeunload warning */
  warnOnBrowserClose?: boolean
  /** Custom warning message for browser close */
  browserCloseMessage?: string
  /** Custom confirm callback (use modal.confirm for custom modals) */
  confirmCallback?: (message: string) => Promise<boolean>
}

/**
 * Hook to track unsaved changes and warn users before leaving
 *
 * @param options Configuration options
 * @returns Object with unsaved changes state and control functions
 *
 * @example
 * ```typescript
 * const { hasUnsavedChanges, markAsUnsaved, markAsSaved, warnBeforeLeaving } = useUnsavedChanges({
 *   warnOnBrowserClose: true
 * })
 *
 * // In your change handler:
 * const handleChange = (data) => {
 *   markAsUnsaved()
 *   // ... update data
 * }
 *
 * // Before navigation:
 * const handleNewDiagram = () => {
 *   if (hasUnsavedChanges) {
 *     const proceed = warnBeforeLeaving()
 *     if (!proceed) return
 *   }
 *   // ... create new diagram
 * }
 *
 * // After saving:
 * const handleSave = () => {
 *   // ... save logic
 *   markAsSaved()
 * }
 * ```
 */
export function useUnsavedChanges(
  options: UseUnsavedChangesOptions = {}
): UseUnsavedChangesReturn {
  const {
    warnOnBrowserClose = true,
    browserCloseMessage = 'You have unsaved changes. Are you sure you want to leave?',
    confirmCallback,
  } = options

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const isInitialMount = useRef(true)

  /**
   * Mark the current state as having unsaved changes
   */
  const markAsUnsaved = useCallback(() => {
    setHasUnsavedChanges(true)
  }, [])

  /**
   * Mark the current state as saved (no unsaved changes)
   */
  const markAsSaved = useCallback(() => {
    setHasUnsavedChanges(false)
  }, [])

  /**
   * Reset the tracking state
   */
  const resetTracking = useCallback(() => {
    setHasUnsavedChanges(false)
    isInitialMount.current = true
  }, [])

  /**
   * Show warning dialog before leaving
   * @param message Custom warning message
   * @returns true if user wants to proceed, false if they want to stay
   * Returns Promise<boolean> if confirmCallback is provided, otherwise boolean
   */
  const warnBeforeLeaving = useCallback(
    (message?: string): boolean | Promise<boolean> => {
      if (!hasUnsavedChanges) return true

      const confirmMessage =
        message ||
        'You have unsaved changes. Creating a new diagram will discard them. Continue?'

      // Use custom confirm callback if provided (returns Promise)
      if (confirmCallback) {
        return confirmCallback(confirmMessage)
      }

      // Fallback to browser confirm (returns boolean)
      return window.confirm(confirmMessage)
    },
    [hasUnsavedChanges, confirmCallback]
  )

  /**
   * Browser beforeunload handler
   * Warns user when trying to close tab/window with unsaved changes
   */
  useEffect(() => {
    if (!warnOnBrowserClose) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Modern browsers ignore custom messages and show their own
        // But we still need to set returnValue for the warning to appear
        e.preventDefault()
        e.returnValue = browserCloseMessage
        return browserCloseMessage
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges, warnOnBrowserClose, browserCloseMessage])

  /**
   * Skip initial mount to avoid marking fresh diagrams as unsaved
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    }
  }, [])

  return {
    hasUnsavedChanges,
    markAsSaved,
    markAsUnsaved,
    warnBeforeLeaving,
    resetTracking,
  }
}
